import { FC, useMemo } from 'react';
import { LocalizedProperty, lt } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { Service, ServiceType } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { sortOrderableEntity } from '@mews-commander/legacy-entities/src/utils/orderableEntities';
import { ProductSelectField } from '../../../../../../components/form/hookFields';
import { FilterValueKey } from '../types';
import { FILTERS_SPACING, ProductCheckListFilterFieldsDataTestId } from '../constants';
import { useServicesProductsContext } from '../../../../contexts/ServicesProductsContext';

const getServiceProductIdsName = (service: Service) => `${FilterValueKey.productIds}.${service.id}`;

const getAdditionalProductIdsName = () => `${FilterValueKey.productIds}.${ServiceType.Additional.toLowerCase()}`;

export const ProductsSectionFields: FC = () => {
    const { productsByServiceType, productCategories } = useServicesProductsContext();

    const additionalServicesProducts = useMemo(
        () => productsByServiceType[ServiceType.Additional].flatMap(({ products }) => products),
        [productsByServiceType]
    );

    return (
        <Stack vertical spacing={FILTERS_SPACING}>
            {[...productsByServiceType[ServiceType.Bookable]]
                .sort((a, b) => sortOrderableEntity(a.service, b.service))
                .filter(({ products }) => !!products.length)
                .map(({ service, products }) => (
                    <ProductSelectField
                        multiselect
                        compact
                        preserveMultiSelectSearch
                        key={service.id}
                        name={getServiceProductIdsName(service)}
                        label={service && <LocalizedProperty property={service.names} />}
                        products={products}
                        productCategories={productCategories}
                        data-test-id={ProductCheckListFilterFieldsDataTestId.productSelectField}
                        data-service-id={service.id}
                    />
                ))}

            {!!additionalServicesProducts.length && (
                <ProductSelectField
                    multiselect
                    compact
                    preserveMultiSelectSearch
                    name={getAdditionalProductIdsName()}
                    label={lt(TextKey.AdditionalServicePlural)}
                    products={additionalServicesProducts}
                    productCategories={productCategories}
                    data-test-id={ProductCheckListFilterFieldsDataTestId.productSelectField}
                    data-test-additional-services
                />
            )}
        </Stack>
    );
};
